import type { CrudModalField } from "@/components/tables/fields/crudModalField";

type Translate = (key: string) => string;
type SelectOptionSource = Pick<
  CrudModalField,
  "entity" | "userEntityValueField" | "showAddressForBuildingSelect"
>;

export interface SelectOption {
  value: any;
  label: any;
}

const isListOfStrings = (items: any[]) =>
  items.every((item) => typeof item === "string");

const buildingLabelWithAddress = (building: any) => {
  const labelParts = [building.name];
  const details = [];
  if (building.street_house !== "") details.push(building.street_house);
  if (building.zip || building.city) {
    details.push(
      [building.zip, building.city].filter((part) => part !== "").join(" ")
    );
  }
  if (details.length > 0) labelParts.push(`(${details.join(", ")})`);
  return labelParts.join(" ");
};

const toSelectOption = (
  item: any,
  source: SelectOptionSource,
  stringItems: boolean,
  t: Translate
): SelectOption => {
  if (stringItems) return { value: item, label: item };

  let value = item.id ? item.id : item.value;
  if (source.entity === "user" && source.userEntityValueField === "subject_id") {
    value = item.subject_id ?? item.id ?? item.value;
  }

  let label = t(item.name) || t(item.label); // fixed options only have a label, and i18next translates an undefined key to ""
  if (source.showAddressForBuildingSelect) label = buildingLabelWithAddress(item);
  if (source.entity === "user") label = item.last_name + ", " + item.first_name;
  if (source.entity === "trackedServiceTemplate") label = item.internal_name;
  return { value, label };
};

export const buildSelectOptions = (
  items: any[] | undefined,
  source: SelectOptionSource,
  t: Translate
): SelectOption[] => {
  if (!items || items.length === 0) return [];
  const stringItems = isListOfStrings(items);
  return items
    .map((item) => toSelectOption(item, source, stringItems, t))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const findOption = (value: unknown, options: SelectOption[]) =>
  options.find((option) => option.value === value);

export const findSelectedOptions = (value: any, options: SelectOption[]) => {
  if (
    typeof value === "string" ||
    typeof value === "boolean" ||
    typeof value === "number"
  ) {
    return findOption(value, options);
  }
  const selectedOptions = [];
  for (const key in value) {
    const option = findOption(value[key], options);
    if (option) selectedOptions.push(option);
  }
  return selectedOptions;
};
